import { InputNumber } from "@/components/InputNumber";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateCommissionsUnit, useUpdateCommissionsUnit } from "../../../../../common/api/queries";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import InstitutionSelect from "../../../../enumerations/components/form/InstitutionSelect";
import { CommissionsBatch, CommissionsUnit, CreateCommissionsUnit, UpdateCommissionsUnit } from "../../types";

interface Props {
  batch: CommissionsBatch;
  unit?: CommissionsUnit;
  open: boolean;
  onFormCancel: () => void;
}

export const CommissionsUnitForm = ({ batch, unit, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateCommissionsUnit | UpdateCommissionsUnit>();

  const createCommissionsUnit = useCreateCommissionsUnit();
  const updateCommissionsUnit = useUpdateCommissionsUnit();

  useEffect(() => {
    if (open && unit) {
      form.setFieldsValue({ ...unit, institutionId: unit.institution.id });
    }
  }, [open, unit, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (unit) {
          updateCommissionsUnit.mutate(
            { id1: batch.id, id2: unit.id, object: values as UpdateCommissionsUnit },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "commissions.batch.attrs.unit", error.violations),
            },
          );
        } else {
          createCommissionsUnit.mutate(
            { id: batch.id, object: values as CreateCommissionsUnit },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "commissions.batch.attrs.unit", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  const colSpan = 8;

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={unit ? t("commissions.batch.titles.updateUnit") : t("commissions.batch.titles.createUnit")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createCommissionsUnit.isPending || updateCommissionsUnit.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="commissionsUnitForm">
        <HiddenInput name="optimisticLockVersion" />

        {!unit && (
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <InstitutionSelect
                formItemProps={{
                  name: "institutionId",
                  label: t("commissions.batch.attrs.unit.institutionId"),
                  rules: [validations.notNull],
                }}
                optionsProps={{
                  groupByType: true,
                  filter: (institution) =>
                    !batch.commissionsUnits.flatMap((u) => u.institution).some((i) => i.id === institution.id),
                }}
              />
            </Col>
          </Row>
        )}

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="bankAccountCommissionAmount"
              label={t("commissions.batch.attrs.unit.bankAccountCommissionAmount")}
              rules={[validations.none]}
            >
              <InputNumber type="decimal" addon="euro" />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="invoiceCommissionAmount"
              label={t("commissions.batch.attrs.unit.invoiceCommissionAmount")}
              rules={[validations.none]}
            >
              <InputNumber type="decimal" addon="euro" />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="volumeCommissionAmount"
              label={t("commissions.batch.attrs.unit.volumeCommissionAmount")}
              rules={[validations.none]}
            >
              <InputNumber type="decimal" addon="euro" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
